/**
 * WidgetPreviewHost - Host para renderizar o Widget real no preview
 * 
 * Renderiza o widget de webchat real (não mockado) dentro do modal de preview,
 * permitindo testar conversas e validar fluxos vinculados.
 * 
 * @version 1.0.0
 */

import React, { useEffect, useRef } from 'react';
import { Widget } from '@/widget/Widget';
import { createLogger } from '@/lib/logger';

// Logger para diagnóstico
const logger = createLogger('WidgetPreviewHost');

interface WidgetPreviewHostProps {
  /** ID da inbox para carregar configuração do widget */
  inboxId: string;
  /** Posição do widget (bottom-right ou bottom-left) */
  position: 'bottom-right' | 'bottom-left';
  /** Se está em modo mobile */
  isMobile: boolean;
}

/**
 * Componente que hospeda o Widget real para preview interativo
 * 
 * Permite:
 * - Enviar mensagens reais via webhook
 * - Receber respostas de fluxos vinculados
 * - Testar botões interativos
 * - Validar configurações antes de publicar
 */
export function WidgetPreviewHost({ inboxId, position, isMobile }: WidgetPreviewHostProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    logger.info('Inicializando preview do widget', { inboxId, position, isMobile });
    
    return () => {
      logger.info('Desmontando preview do widget');
    };
  }, [inboxId, position, isMobile]);

  // Valida se temos o inbox_id necessário
  if (!inboxId) {
    logger.warn('inbox_id não fornecido para o preview');
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>Configuração incompleta. Salve as configurações primeiro.</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full"
      style={{
        // Isola estilos do widget dentro do container
        contain: 'layout style',
      }}
    >
      {/* 
        Renderiza o Widget real
        - O widget irá buscar sua própria configuração via useWidgetConfig
        - Conexões realtime serão estabelecidas via useWebchatConnection
        - Mensagens serão enviadas via useMessageQueue -> webchat-webhook
      */}
      <Widget 
        config={{
          inboxId: inboxId,
          // Não passamos visitor para permitir formulário pré-chat se configurado
        }}
      />
    </div>
  );
}
